import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, EventEmitter, inject, OnInit, OnDestroy, Output, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DynamicFormService } from 'src/app/services/dynamic-form.service';
import { UserManualService } from 'src/app/services/user-manual.service';
import { UtilService } from 'src/app/services/util.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  FormFieldConditionLogic,
  FormFieldConditionRule,
  isFieldPermanentlyHidden,
  shouldShowFieldByConditions
} from 'src/app/shared/utils/form-field-condition.util';

export interface CustomField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  required?: boolean;
}

export interface FormField {
  type: string;
  name: string;
  label: string;
  placeholder?: string;
  value?: any;
  required?: boolean;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  multiple?: boolean;
  accept?: string;
  columns?: string;
  prefix?: string;
  suffix?: string;
  hideIf?: string;
  conditionFieldName?: string;
  conditionAction?: 'show' | 'hide';
  conditions?: FormFieldConditionRule[];
  conditionLogic?: FormFieldConditionLogic;
  disabled?: boolean;
  readOnly?: boolean;
  incidentBindUser?: boolean;
  incidentBindTeam?: boolean;
  incidentBindSpeaker?: boolean;
  incidentBindOperator?: boolean;
  optionCustomFields?: { [option: string]: CustomField[] };
  errorMessages?: {
    required?: string;
    email?: string;
    pattern?: string;
    minlength?: string;
    maxlength?: string;
    min?: string;
    max?: string;
  };
}

export interface FormSection {
  id: string;
  title: string;
  fields: FormField[];
}

@Component({
  selector: 'app-dynamic-form-model',
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './dynamic-form-model.component.html',
  styleUrl: './dynamic-form-model.component.scss'
})
export class DynamicFormModelComponent implements OnInit, OnDestroy {

  private fb = inject(UntypedFormBuilder);
  private modalService = inject(BsModalService);
  private _utils = inject(UtilService);
  private dynamicFormService = inject(DynamicFormService);
  protected manual = inject(UserManualService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  form: UntypedFormGroup;
  formSections: FormSection[] = [];
  unsectionedFields: FormField[] = [];
  selectedSectionIndex: number = 0;
  sectionExpanded: { [sectionId: string]: boolean } = {};
  editingSectionIndex: number | null = null;
  editingFieldIndex: number | null = null;
  fieldConfig: FormField = {} as FormField;
  isLoading: boolean = false;
  saveSubscription: Subscription;
  isEditMode: boolean = false;
  formId: string | null = null;
  formTemplateName = '';
  isIncidentFormType = false;
  getFormSubscription: Subscription;
  formValueChangesSubscription: Subscription;
  formSubmitted: boolean = false;
  @Output() formSaved = new EventEmitter<{ data: any, fields: FormField[] }>();

  get formFields(): FormField[] {
    return this.formSections.length > 0
      ? this.formSections.flatMap(s => s.fields)
      : this.unsectionedFields;
  }

  draggedSectionIndex: number | null = null;
  draggedFieldIndex: number | null = null;
  dragOverSectionIndex: number | null = null;
  dragOverFieldIndex: number | null = null;
  draggedIndex: number | null = null;
  dragOverIndex: number | null = null;
  multiSelectOpen: { [key: string]: boolean } = {};
  multiSelectSearch: { [key: string]: string } = {};
  readonly baseConditionValueOptions: Array<{ value: string; label: string }> = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: '__any__', label: 'Any value' },
    { value: '__has_value__', label: 'Has value' },
    { value: '__empty__', label: 'Is empty' }
  ];
  expandedSections: {
    condition: boolean;
    layout: boolean;
    appearance: boolean;
    validation: boolean;
  } = {
      condition: false,
      layout: false,
      appearance: false,
      validation: false
    };
  columnOptions = [
    { value: 'auto', label: 'Auto' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
  ];
  availableFieldTypes = [
    { type: 'text', label: 'Text Input', icon: 'fa fa-font' },
    { type: 'textarea', label: 'Textarea', icon: 'fa fa-file-text-o' },
    { type: 'number', label: 'Number', icon: 'fa fa-hashtag' },
    { type: 'email', label: 'Email', icon: 'fa fa-envelope' },
    { type: 'password', label: 'Password', icon: 'fa fa-lock' },
    { type: 'tel', label: 'Phone', icon: 'fa fa-phone' },
    { type: 'url', label: 'URL', icon: 'fa fa-link' },
    { type: 'date', label: 'Date', icon: 'fa fa-calendar' },
    { type: 'time', label: 'Time', icon: 'fa fa-clock-o' },
    { type: 'datetime-local', label: 'Date & Time', icon: 'fa fa-calendar-check-o' },
    { type: 'checkbox', label: 'Checkbox', icon: 'fa fa-check-square-o' },
    { type: 'radio', label: 'Radio', icon: 'fa fa-circle-o' },
    { type: 'select', label: 'Dropdown', icon: 'fa fa-list' },
    { type: 'file', label: 'File Upload', icon: 'fa fa-upload' },
    { type: 'range', label: 'Range', icon: 'fa fa-sliders' },
    { type: 'color', label: 'Color Picker', icon: 'fa fa-paint-brush' },
    { type: 'hidden', label: 'Hidden', icon: 'fa fa-eye-slash' }
  ];

  ngOnInit() {
    this.form = this.fb.group({});
    this.resetFieldConfig();
    this.formSubmitted = false;
    this.route.queryParams.subscribe(params => {
      if (params['id'] && params['edit']) {
        this.isEditMode = true;
        this.formId = params['id'];
        this.loadFormForEdit();
      }
    });

    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['formData']) {
      const formData = navigation.extras.state['formData'];
      const raw = this.unwrapFormDataFromBackend(formData.form_data || []);
      const { sections, unsectioned } = this.normalizeToSectionsAndUnsectioned(raw);
      this.formSections = sections;
      this.unsectionedFields = unsectioned;
      this.formId = formData._id;
      this.isEditMode = true;
      this.formTemplateName = formData.form_name || formData.name || formData.title || '';
      this.isIncidentFormType = formData.is_incident_form_type === true;
      this.buildFormFromFields();
      this.setSectionExpandedDefaults();
    } else {
      if (this.formValueChangesSubscription) {
        this.formValueChangesSubscription.unsubscribe();
      }
      this.formValueChangesSubscription = this.form.valueChanges.subscribe(() => {
        this.cdr.markForCheck();
      });
    }
  }

  unwrapFormDataFromBackend(raw: any): any {
    if (!raw) return raw;
    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0];
      if (first && first.form_data && first.form_data.sections && Array.isArray(first.form_data.sections)) {
        return first.form_data;
      }
      if (first && first.sections && Array.isArray(first.sections)) {
        return first;
      }
      const hasSections = raw.some((item: any) => item && item.form_data && item.form_data.sections);
      if (hasSections) {
        const found = raw.find((item: any) => item && item.form_data && item.form_data.sections);
        return found ? found.form_data : raw;
      }
    }
    if (raw.form_data && raw.form_data.sections && Array.isArray(raw.form_data.sections)) {
      return raw.form_data;
    }
    return raw;
  }

  normalizeToSections(formData: any): FormSection[] {
    const { sections } = this.normalizeToSectionsAndUnsectioned(formData);
    return sections;
  }

  normalizeToSectionsAndUnsectioned(formData: any): { sections: FormSection[]; unsectioned: FormField[] } {
    if (!formData) return { sections: [], unsectioned: [] };
    if (formData.sections && Array.isArray(formData.sections)) {
      const sections = formData.sections.map((s: any) => ({
        id: s.id || `sec_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        title: s.title || 'Section',
        fields: (s.fields || []).map((f: FormField) => this.normalizeField(f))
      }));
      const unsectioned: FormField[] = [];
      if (sections.length === 1 && (sections[0].id === 'unsectioned' || sections[0].title === '')) {
        unsectioned.push(...sections[0].fields);
        return { sections: [], unsectioned };
      }
      return { sections, unsectioned };
    }
    const fields = Array.isArray(formData) ? formData : [];
    const normalized = fields.map((f: FormField) => this.normalizeField(f));
    if (normalized.length === 0) return { sections: [], unsectioned: [] };
    return {
      sections: [{
        id: 'sec_default',
        title: 'Form Fields',
        fields: normalized
      }],
      unsectioned: []
    };
  }

  normalizeField(f: FormField): FormField {
    const field = { ...f };
    if (!field.errorMessages) field.errorMessages = {};
    if (field.conditionFieldName === undefined) field.conditionFieldName = '';
    if (field.conditionAction === undefined) field.conditionAction = 'hide';
    if (!field.conditions?.length && field.conditionFieldName) {
      field.conditions = [{
        fieldName: field.conditionFieldName,
        action: field.conditionAction || 'show',
        expectedValue: '__any__'
      }];
    }
    if (!field.conditionLogic) field.conditionLogic = 'and';
    if (field.incidentBindUser === undefined) field.incidentBindUser = false;
    if (field.incidentBindTeam === undefined) field.incidentBindTeam = false;
    if (field.incidentBindSpeaker === undefined) field.incidentBindSpeaker = false;
    if (field.incidentBindOperator === undefined) field.incidentBindOperator = false;
    return field;
  }

  setSectionExpandedDefaults() {
    this.formSections.forEach(s => {
      if (this.sectionExpanded[s.id] === undefined) {
        this.sectionExpanded[s.id] = true;
      }
    });
  }

  loadFormForEdit() {
    if (!this.formId) return;

    this.isLoading = true;
    this.getFormSubscription = this.dynamicFormService.getDynamicFormById(this.formId).subscribe({
      next: (res) => {
        this.isLoading = false;

        let rawFormData: any = null;
        const applyNameFromDoc = (d: any) => {
          const n = (d?.form_name || d?.name || d?.title || '').trim();
          if (n) this.formTemplateName = n;
        };
        const applyIncidentFlagFromDoc = (d: any) => {
          if (d && typeof d === 'object') {
            this.isIncidentFormType = d.is_incident_form_type === true;
          }
        };
        if (res?.dynamicForm && !Array.isArray(res.dynamicForm) && res.dynamicForm.form_data != null) {
          rawFormData = JSON.parse(JSON.stringify(res.dynamicForm.form_data));
          applyNameFromDoc(res.dynamicForm);
          applyIncidentFlagFromDoc(res.dynamicForm);
        } else if (res?.dynamicForm && Array.isArray(res.dynamicForm) && res.dynamicForm.length > 0) {
          const d = res.dynamicForm[0];
          rawFormData = JSON.parse(JSON.stringify(d.form_data || []));
          applyNameFromDoc(d);
          applyIncidentFlagFromDoc(d);
        } else if (res?.form_data != null) {
          rawFormData = JSON.parse(JSON.stringify(res.form_data));
          applyNameFromDoc(res);
          applyIncidentFlagFromDoc(res);
        }
        if (!rawFormData) {
          this._utils.showErrorInfo('Form data not found');
          return;
        }
        rawFormData = this.unwrapFormDataFromBackend(rawFormData);
        const { sections, unsectioned } = this.normalizeToSectionsAndUnsectioned(rawFormData);
        this.formSections = sections;
        this.unsectionedFields = unsectioned;
        this.setSectionExpandedDefaults();
        this.buildFormFromFields();
      },
      error: (e) => {
        this.isLoading = false;
        this._utils.showErrorInfo('Failed to load form. Please try again.');
      },
      complete: () => {
        if (this.getFormSubscription) {
          this.getFormSubscription.unsubscribe();
        }
      }
    });
  }

  buildFormFromFields() {
    this.form = this.fb.group({});
    this.formFields.forEach(field => {
      this.addFormControl(field);
    });
    this.formSubmitted = false;

    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
    this.formValueChangesSubscription = this.form.valueChanges.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  constructor() {
    this._utils.sidebarAndHeaderConfig({
      header: true,
      sidebar: true,
    });
  }

  resetFieldConfig() {
    this.fieldConfig = {
      type: 'text',
      name: '',
      label: '',
      placeholder: '',
      value: '',
      required: false,
      options: [],
      multiple: false,
      columns: 'auto',
      prefix: '',
      suffix: '',
      hideIf: '',
      conditionFieldName: '',
      conditionAction: 'hide',
      conditions: [],
      conditionLogic: 'and',
      disabled: false,
      readOnly: false,
      incidentBindUser: false,
      incidentBindTeam: false,
      incidentBindSpeaker: false,
      incidentBindOperator: false,
      optionCustomFields: {},
      errorMessages: {}
    };
  }

  closeModal() {
    this.modalService.hide();
  }

  addSection() {
    if (this.unsectionedFields.length > 0) {
      const id = `sec_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      this.formSections.push({
        id,
        title: 'Section 1',
        fields: [...this.unsectionedFields]
      });
      this.unsectionedFields.forEach(f => this.form.removeControl(f.name));
      this.unsectionedFields = [];
      this.formSections[0].fields.forEach(f => this.addFormControl(f));
      this.sectionExpanded[id] = true;
      this.selectedSectionIndex = 0;
    } else {
      const id = `sec_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      this.formSections.push({
        id,
        title: `Section ${this.formSections.length + 1}`,
        fields: []
      });
      this.sectionExpanded[id] = true;
      this.selectedSectionIndex = this.formSections.length - 1;
    }
    this.cdr.markForCheck();
  }

  removeSection(sectionIndex: number) {
    const section = this.formSections[sectionIndex];
    section.fields.forEach(f => this.form.removeControl(f.name));
    this.formSections.splice(sectionIndex, 1);
    delete this.sectionExpanded[section.id];
    if (this.editingSectionIndex === sectionIndex) {
      this.cancelEdit();
    } else if (this.editingSectionIndex !== null && this.editingSectionIndex > sectionIndex) {
      this.editingSectionIndex--;
    }
    if (this.selectedSectionIndex >= this.formSections.length) {
      this.selectedSectionIndex = Math.max(0, this.formSections.length - 1);
    } else if (this.selectedSectionIndex > sectionIndex) {
      this.selectedSectionIndex--;
    }
    this.cdr.markForCheck();
  }

  toggleSectionExpand(sectionId: string) {
    this.sectionExpanded[sectionId] = !this.sectionExpanded[sectionId];
  }

  isSectionExpanded(sectionId: string): boolean {
    return this.sectionExpanded[sectionId] !== false;
  }

  get allSectionsCollapsed(): boolean {
    return this.formSections.length > 0 && this.formSections.every(s => !this.sectionExpanded[s.id]);
  }

  collapseExpandAll() {
    const collapse = !this.allSectionsCollapsed;
    this.formSections.forEach(s => {
      this.sectionExpanded[s.id] = !collapse;
    });
    this.cdr.markForCheck();
  }

  addField(type: string) {
    const fieldName = this.generateFieldName(type);
    const newField: FormField = {
      type,
      name: fieldName,
      label: this.getFieldLabel(type),
      placeholder: `Enter ${this.getFieldLabel(type).toLowerCase()}`,
      value: this.getDefaultValue(type),
      required: false,
      options: (type === 'select' || type === 'radio') ? ['Option 1', 'Option 2', 'Option 3'] : undefined,
      multiple: type === 'select' ? false : undefined,
      min: type === 'number' || type === 'range' ? 0 : undefined,
      max: type === 'number' || type === 'range' ? 100 : undefined,
      step: type === 'number' || type === 'range' ? 1 : undefined,
      accept: type === 'file' ? '*' : undefined,
      columns: 'auto',
      prefix: '',
      suffix: '',
      conditionFieldName: '',
      conditionAction: 'hide',
      conditions: [],
      conditionLogic: 'and',
      errorMessages: {}
    };
    if (this.formSections.length === 0) {
      this.unsectionedFields.push(newField);
      this.addFormControl(newField);
      this.editFieldUnsectioned(this.unsectionedFields.length - 1);
    } else {
      const si = Math.min(this.selectedSectionIndex, this.formSections.length - 1);
      const section = this.formSections[si];
      section.fields.push(newField);
      this.addFormControl(newField);
      this.editField(si, section.fields.length - 1);
    }
  }

  generateFieldName(type: string): string {
    const baseName = type.replace('-', '_');
    let counter = 1;
    let name = `${baseName}_${counter}`;
    while (this.formFields.some(f => f.name === name)) {
      counter++;
      name = `${baseName}_${counter}`;
    }
    return name;
  }

  getFieldLabel(type: string): string {
    const fieldType = this.availableFieldTypes.find(ft => ft.type === type);
    return fieldType ? fieldType.label : type.charAt(0).toUpperCase() + type.slice(1);
  }

  getDefaultValue(type: string): any {
    switch (type) {
      case 'checkbox':
        return false;
      case 'number':
      case 'range':
        return 0;
      case 'color':
        return '#000000';
      case 'date':
        return new Date().toISOString().split('T')[0];
      case 'time':
        return '00:00';
      case 'datetime-local':
        return new Date().toISOString().slice(0, 16);
      default:
        return '';
    }
  }

  addFormControl(field: FormField) {
    const validators = this.buildValidators(field);
    let defaultValue = field.value || '';
    if (field.type === 'select' && field.multiple) {
      defaultValue = Array.isArray(field.value) ? field.value : [];
    }
    const control = new UntypedFormControl(defaultValue, validators);
    if (field.disabled) {
      control.disable();
    }
    control.markAsUntouched();
    control.markAsPristine();
    this.form.addControl(field.name, control);
  }

  buildValidators(field: FormField): any[] {
    const validators: any[] = [];
    if (field.required) {
      validators.push(Validators.required);
    }
    if (field.type === 'email') {
      validators.push(Validators.email);
    }
    if (field.pattern) {
      validators.push(Validators.pattern(field.pattern));
    }
    if (field.minLength) {
      validators.push(Validators.minLength(field.minLength));
    }
    if (field.maxLength) {
      validators.push(Validators.maxLength(field.maxLength));
    }
    if (field.min !== undefined && (field.type === 'number' || field.type === 'range')) {
      validators.push(Validators.min(field.min));
    }
    if (field.max !== undefined && (field.type === 'number' || field.type === 'range')) {
      validators.push(Validators.max(field.max));
    }
    return validators;
  }

  readonly UNSECTIONED_INDEX = -1;

  editFieldUnsectioned(fieldIndex: number) {
    this.editField(this.UNSECTIONED_INDEX, fieldIndex);
  }

  editField(sectionIndex: number, fieldIndex: number) {
    this.editingSectionIndex = sectionIndex;
    this.editingFieldIndex = fieldIndex;
    const source = sectionIndex === this.UNSECTIONED_INDEX
      ? this.unsectionedFields[fieldIndex]
      : this.formSections[sectionIndex].fields[fieldIndex];
    this.fieldConfig = {
      ...source,
      conditions: this.cloneConditionRules(source.conditions)
    };
    if (!this.fieldConfig.options) {
      this.fieldConfig.options = [];
    }
    if (!this.fieldConfig.columns) {
      this.fieldConfig.columns = 'auto';
    }
    if (this.fieldConfig.prefix === undefined) {
      this.fieldConfig.prefix = '';
    }
    if (this.fieldConfig.suffix === undefined) {
      this.fieldConfig.suffix = '';
    }
    if (this.fieldConfig.hideIf === undefined) {
      this.fieldConfig.hideIf = '';
    }
    if (this.fieldConfig.conditionFieldName === undefined) {
      this.fieldConfig.conditionFieldName = '';
    }
    if (this.fieldConfig.conditionAction === undefined) {
      this.fieldConfig.conditionAction = 'hide';
    }
    this.ensureFieldConfigConditions();
    if (this.fieldConfig.disabled === undefined) {
      this.fieldConfig.disabled = false;
    }
    if (this.fieldConfig.readOnly === undefined) {
      this.fieldConfig.readOnly = false;
    }
    if (this.fieldConfig.incidentBindUser === undefined) {
      this.fieldConfig.incidentBindUser = false;
    }
    if (this.fieldConfig.incidentBindTeam === undefined) {
      this.fieldConfig.incidentBindTeam = false;
    }
    if (this.fieldConfig.incidentBindSpeaker === undefined) {
      this.fieldConfig.incidentBindSpeaker = false;
    }
    if (this.fieldConfig.incidentBindOperator === undefined) {
      this.fieldConfig.incidentBindOperator = false;
    }
    if (!this.fieldConfig.optionCustomFields) {
      this.fieldConfig.optionCustomFields = {};
    }
    if (!this.fieldConfig.errorMessages) {
      this.fieldConfig.errorMessages = {};
    }
    this.formSubmitted = false;

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
      }
    });

    this.expandedSections = {
      condition: false,
      layout: false,
      appearance: false,
      validation: false
    };
  }

  updateFieldInPreview(sectionIndex: number, fieldIndex: number) {
    if (this.editingSectionIndex === null || this.editingFieldIndex === null || this.editingSectionIndex !== sectionIndex || this.editingFieldIndex !== fieldIndex) {
      return;
    }
    const isUnsectioned = sectionIndex === this.UNSECTIONED_INDEX;
    const oldField = isUnsectioned ? this.unsectionedFields[fieldIndex] : this.formSections[sectionIndex].fields[fieldIndex];
    const newField = {
      ...this.fieldConfig,
      conditions: this.cloneConditionRules(this.fieldConfig.conditions)
    };

    newField.options = this.fieldConfig.options ? [...this.fieldConfig.options] : [];
    if (this.fieldConfig.optionCustomFields) {
      newField.optionCustomFields = JSON.parse(JSON.stringify(this.fieldConfig.optionCustomFields));
    }
    if (!newField.errorMessages) {
      newField.errorMessages = {};
    }
    const wasTouched = this.form.get(oldField.name)?.touched || false;

    if (oldField.name !== newField.name) {
      this.form.removeControl(oldField.name);
      this.addFormControl(newField);
    } else {
      const control = this.form.get(newField.name);
      if (control) {
        const validators = this.buildValidators(newField);
        control.setValidators(validators);
        if (!wasTouched) {
          control.markAsUntouched();
          control.markAsPristine();
        }
        control.updateValueAndValidity({ emitEvent: false });
        if (newField.disabled) {
          control.disable();
        } else {
          control.enable();
        }
      }
    }
    if (isUnsectioned) {
      this.unsectionedFields[fieldIndex] = newField;
    } else {
      this.formSections[sectionIndex].fields[fieldIndex] = newField;
    }
    this.fieldConfig = {
      ...newField,
      options: newField.options ? [...newField.options] : [],
      conditions: this.cloneConditionRules(newField.conditions)
    };
    this.formSubmitted = false;
  }

  saveFieldConfig() {
    if (this.editingSectionIndex === null || this.editingFieldIndex === null || !this.fieldConfig.name || !this.fieldConfig.label) {
      return;
    }
    const si = this.editingSectionIndex;
    const fi = this.editingFieldIndex;
    const isUnsectioned = si === this.UNSECTIONED_INDEX;
    const oldField = isUnsectioned ? this.unsectionedFields[fi] : this.formSections[si].fields[fi];
    const newField = { ...this.fieldConfig };
    if (this.fieldConfig.optionCustomFields) {
      newField.optionCustomFields = JSON.parse(JSON.stringify(this.fieldConfig.optionCustomFields));
    }
    if (!newField.errorMessages) {
      newField.errorMessages = {};
    }
    if (oldField.name !== newField.name) {
      this.form.removeControl(oldField.name);
      this.addFormControl(newField);
    } else {
      const control = this.form.get(newField.name);
      if (control) {
        const validators = this.buildValidators(newField);
        control.setValidators(validators);
        control.updateValueAndValidity();
      }
    }
    if (isUnsectioned) {
      this.unsectionedFields[fi] = newField;
    } else {
      this.formSections[si].fields[fi] = newField;
    }
    this.formSubmitted = false;
    this.cancelEdit();
  }

  cancelEdit() {
    if (this.editingFieldIndex !== null) {
      this.syncOptionsToPreview();
    }
    this.editingSectionIndex = null;
    this.editingFieldIndex = null;
    this.resetFieldConfig();
  }

  removeField(sectionIndex: number, fieldIndex: number) {
    if (sectionIndex === this.UNSECTIONED_INDEX) {
      const field = this.unsectionedFields[fieldIndex];
      this.form.removeControl(field.name);
      this.unsectionedFields.splice(fieldIndex, 1);
      if (this.editingSectionIndex === this.UNSECTIONED_INDEX && this.editingFieldIndex === fieldIndex) {
        this.cancelEdit();
      } else if (this.editingSectionIndex === this.UNSECTIONED_INDEX && this.editingFieldIndex !== null && this.editingFieldIndex > fieldIndex) {
        this.editingFieldIndex--;
      }
      this.cdr.markForCheck();
      return;
    }
    const section = this.formSections[sectionIndex];
    const field = section.fields[fieldIndex];
    this.form.removeControl(field.name);
    section.fields.splice(fieldIndex, 1);
    if (this.editingSectionIndex === sectionIndex && this.editingFieldIndex === fieldIndex) {
      this.cancelEdit();
    } else if (this.editingSectionIndex === sectionIndex && this.editingFieldIndex !== null && this.editingFieldIndex > fieldIndex) {
      this.editingFieldIndex--;
    }
  }

  addOption() {
    if (!this.fieldConfig.options) {
      this.fieldConfig.options = [];
    }
    const newOption = `Option ${this.fieldConfig.options.length + 1}`;
    this.fieldConfig.options.push(newOption);
    this.syncOptionsToPreview();
  }

  removeOption(optionIndex: number) {
    if (this.fieldConfig.options) {
      const removedOption = this.fieldConfig.options[optionIndex];
      this.fieldConfig.options.splice(optionIndex, 1);
      if (this.fieldConfig.optionCustomFields && this.fieldConfig.optionCustomFields[removedOption]) {
        delete this.fieldConfig.optionCustomFields[removedOption];
      }
      this.syncOptionsToPreview();
    }
  }

  getCustomFieldsForOption(option: string): CustomField[] {
    if (!this.fieldConfig.optionCustomFields) {
      this.fieldConfig.optionCustomFields = {};
    }
    if (!this.fieldConfig.optionCustomFields[option]) {
      this.fieldConfig.optionCustomFields[option] = [];
    }
    return this.fieldConfig.optionCustomFields[option];
  }

  addCustomFieldToOption(option: string) {
    if (!this.fieldConfig.optionCustomFields) {
      this.fieldConfig.optionCustomFields = {};
    }
    if (!this.fieldConfig.optionCustomFields[option]) {
      this.fieldConfig.optionCustomFields[option] = [];
    }
    const fieldCount = this.fieldConfig.optionCustomFields[option].length + 1;
    const newField: CustomField = {
      name: `${this.fieldConfig.name}_${option.replace(/\s+/g, '_')}_custom_${fieldCount}`,
      label: `Custom Field ${fieldCount}`,
      type: 'text',
      placeholder: 'Enter value',
      required: false
    };
    this.fieldConfig.optionCustomFields[option].push(newField);
    this.syncOptionsToPreview();
  }

  removeCustomFieldFromOption(option: string, fieldIndex: number) {
    if (this.fieldConfig.optionCustomFields && this.fieldConfig.optionCustomFields[option]) {
      this.fieldConfig.optionCustomFields[option].splice(fieldIndex, 1);
      this.syncOptionsToPreview();
    }
  }

  syncOptionsToPreview() {
    if (this.editingSectionIndex === null || this.editingFieldIndex === null) return;
    if (this.editingSectionIndex === this.UNSECTIONED_INDEX) {
      const f = this.unsectionedFields[this.editingFieldIndex];
      if (f) {
        f.options = [...(this.fieldConfig.options || [])];
        if (this.fieldConfig.optionCustomFields) {
          f.optionCustomFields = JSON.parse(JSON.stringify(this.fieldConfig.optionCustomFields));
        }
      }
    } else {
      const section = this.formSections[this.editingSectionIndex];
      if (section && section.fields[this.editingFieldIndex]) {
        section.fields[this.editingFieldIndex].options = [...(this.fieldConfig.options || [])];
        if (this.fieldConfig.optionCustomFields) {
          section.fields[this.editingFieldIndex].optionCustomFields = JSON.parse(JSON.stringify(this.fieldConfig.optionCustomFields));
        }
      }
    }
  }

  trackByOptionIndex(index: number, item: string): any {
    return index;
  }

  moveField(sectionIndex: number, fieldIndex: number, direction: 'up' | 'down') {
    const section = this.formSections[sectionIndex];
    if (!section) return;
    if (direction === 'up' && fieldIndex > 0) {
      [section.fields[fieldIndex], section.fields[fieldIndex - 1]] = [section.fields[fieldIndex - 1], section.fields[fieldIndex]];
      if (this.editingSectionIndex === sectionIndex && this.editingFieldIndex === fieldIndex) {
        this.editingFieldIndex = fieldIndex - 1;
      } else if (this.editingSectionIndex === sectionIndex && this.editingFieldIndex === fieldIndex - 1) {
        this.editingFieldIndex = fieldIndex;
      }
    } else if (direction === 'down' && fieldIndex < section.fields.length - 1) {
      [section.fields[fieldIndex], section.fields[fieldIndex + 1]] = [section.fields[fieldIndex + 1], section.fields[fieldIndex]];
      if (this.editingSectionIndex === sectionIndex && this.editingFieldIndex === fieldIndex) {
        this.editingFieldIndex = fieldIndex + 1;
      } else if (this.editingSectionIndex === sectionIndex && this.editingFieldIndex === fieldIndex + 1) {
        this.editingFieldIndex = fieldIndex;
      }
    }
    this.rebuildFormControls();
  }

  rebuildFormControls() {
    this.form = this.fb.group({});
    this.formFields.forEach(field => this.addFormControl(field));
  }

  getColumnValue(columns: string | undefined): number {
    if (!columns || columns === 'auto') {
      return 12;
    }
    const val = parseInt(columns);
    return isNaN(val) ? 12 : val;
  }

  getFieldRow(index: number): number {
    let currentRow = 0;
    let currentRowWidth = 0;

    for (let i = 0; i < index; i++) {
      if (i < this.formFields.length) {
        const field = this.formFields[i];
        const cols = this.getColumnValue(field.columns);
        currentRowWidth += cols;

        if (currentRowWidth >= 12) {
          currentRow++;
          currentRowWidth = currentRowWidth % 12;
        }
      }
    }
    return currentRow;
  }


  onDragStart(event: DragEvent, sectionIndex: number, fieldIndex: number) {
    this.draggedSectionIndex = sectionIndex;
    this.draggedFieldIndex = fieldIndex;
    this.draggedIndex = this.getFlatIndex(sectionIndex, fieldIndex);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', `${sectionIndex}-${fieldIndex}`);
    }
    const target = event.currentTarget as HTMLElement;
    if (target) target.classList.add('dragging');
  }

  onDragEnd(event: DragEvent) {
    const target = event.currentTarget as HTMLElement;
    if (target) {
      target.classList.remove('dragging');
      target.style.opacity = '';
      target.style.cursor = '';
    }
    this.draggedSectionIndex = null;
    this.draggedFieldIndex = null;
    this.draggedIndex = null;
    this.dragOverSectionIndex = null;
    this.dragOverFieldIndex = null;
  }

  onDragOver(event: DragEvent, sectionIndex: number, fieldIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    if (this.draggedSectionIndex !== null && this.draggedFieldIndex !== null &&
      (this.draggedSectionIndex !== sectionIndex || this.draggedFieldIndex !== fieldIndex)) {
      this.dragOverSectionIndex = sectionIndex;
      this.dragOverFieldIndex = fieldIndex;
    }
  }

  onDragLeave(event: DragEvent) {
    const currentTarget = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
      this.dragOverSectionIndex = null;
      this.dragOverFieldIndex = null;
    }
  }

  getFlatIndex(sectionIndex: number, fieldIndex: number): number {
    if (sectionIndex === this.UNSECTIONED_INDEX) return fieldIndex;
    let idx = 0;
    for (let s = 0; s < this.formSections.length; s++) {
      if (s === sectionIndex) return idx + fieldIndex;
      idx += this.formSections[s].fields.length;
    }
    return idx;
  }

  onDrop(event: DragEvent, dropSectionIndex: number, dropFieldIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    if (this.draggedSectionIndex === null || this.draggedFieldIndex === null) {
      this.draggedSectionIndex = null;
      this.draggedFieldIndex = null;
      this.dragOverSectionIndex = null;
      this.dragOverFieldIndex = null;
      return;
    }
    const ds = this.draggedSectionIndex;
    const df = this.draggedFieldIndex;
    if (ds === dropSectionIndex && df === dropFieldIndex) {
      this.draggedSectionIndex = null;
      this.draggedFieldIndex = null;
      this.dragOverSectionIndex = null;
      this.dragOverFieldIndex = null;
      return;
    }

    if (ds === this.UNSECTIONED_INDEX && dropSectionIndex === this.UNSECTIONED_INDEX) {
      const draggedField = this.unsectionedFields[df];
      const draggedValue = this.form.get(draggedField.name)?.value;
      const [removed] = this.unsectionedFields.splice(df, 1);
      let insertIdx = dropFieldIndex > df ? dropFieldIndex - 1 : dropFieldIndex;
      this.unsectionedFields.splice(insertIdx, 0, removed);
      this.rebuildFormControls();
      const ctrl = this.form.get(removed.name);
      if (ctrl && draggedValue !== null && draggedValue !== undefined) ctrl.setValue(draggedValue);
      if (this.editingSectionIndex === this.UNSECTIONED_INDEX && this.editingFieldIndex === df) {
        this.editingFieldIndex = insertIdx;
      } else if (this.editingSectionIndex === this.UNSECTIONED_INDEX && this.editingFieldIndex !== null) {
        if (df < this.editingFieldIndex && insertIdx >= this.editingFieldIndex) this.editingFieldIndex--;
        else if (df > this.editingFieldIndex && insertIdx <= this.editingFieldIndex) this.editingFieldIndex++;
      }
      this.draggedSectionIndex = null;
      this.draggedFieldIndex = null;
      this.dragOverSectionIndex = null;
      this.dragOverFieldIndex = null;
      return;
    }

    if (ds === this.UNSECTIONED_INDEX || dropSectionIndex === this.UNSECTIONED_INDEX) {
      this.draggedSectionIndex = null;
      this.draggedFieldIndex = null;
      this.dragOverSectionIndex = null;
      this.dragOverFieldIndex = null;
      return;
    }

    const dragSection = this.formSections[ds];
    const draggedField = dragSection.fields[df];
    const draggedValue = this.form.get(draggedField.name)?.value;
    const dropSection = this.formSections[dropSectionIndex];

    const [removed] = dragSection.fields.splice(df, 1);
    let insertIdx = dropSectionIndex === ds && dropFieldIndex > df ? dropFieldIndex - 1 : dropFieldIndex;
    if (ds === dropSectionIndex && insertIdx > dropSection.fields.length) insertIdx = dropSection.fields.length;
    dropSection.fields.splice(insertIdx, 0, removed);

    this.rebuildFormControls();
    const ctrl = this.form.get(draggedField.name);
    if (ctrl && draggedValue !== null && draggedValue !== undefined) ctrl.setValue(draggedValue);

    if (this.editingSectionIndex === ds && this.editingFieldIndex === df) {
      this.editingSectionIndex = dropSectionIndex;
      this.editingFieldIndex = insertIdx;
    } else if (this.editingSectionIndex === dropSectionIndex && this.editingFieldIndex !== null) {
      if (ds === dropSectionIndex && df < this.editingFieldIndex && insertIdx >= this.editingFieldIndex) {
        this.editingFieldIndex--;
      } else if (ds === dropSectionIndex && df > this.editingFieldIndex && insertIdx <= this.editingFieldIndex) {
        this.editingFieldIndex++;
      } else if (ds !== dropSectionIndex && this.editingSectionIndex === ds && this.editingFieldIndex > df) {
        this.editingFieldIndex--;
      }
    }

    this.draggedSectionIndex = null;
    this.draggedFieldIndex = null;
    this.dragOverSectionIndex = null;
    this.dragOverFieldIndex = null;
  }

  onSubmit() {
    this.formSubmitted = true;
    const formData = this.form.value;

    const sectionsToSave = this.formSections.length > 0
      ? this.formSections
      : (this.unsectionedFields.length > 0 ? [{ id: 'unsectioned', title: '', fields: this.unsectionedFields }] : []);
    const payload: {
      form_data: { sections: typeof sectionsToSave };
      is_incident_form_type?: boolean;
      form_name?: string;
      name?: string;
    } = {
      form_data: { sections: sectionsToSave }
    };
    payload.is_incident_form_type = this.isIncidentFormType;
    const trimmedName = (this.formTemplateName ?? '').trim();
    if (trimmedName) {
      payload.form_name = trimmedName;
      payload.name = trimmedName;
    }
    const apiCall = this.isEditMode && this.formId
      ? this.dynamicFormService.updateDynamicForm(this.formId, payload)
      : this.dynamicFormService.addDynamicForm(payload);

    this.saveSubscription = apiCall.subscribe({
      next: (res) => {
        if (res) {
          const wasNew = !this.isEditMode;
          const doc = res.dynamicForm ?? res.data ?? res;
          const savedName = (doc?.form_name || doc?.name || doc?.title || '').trim();
          if (savedName) {
            this.formTemplateName = savedName;
          }
          if (wasNew && (doc?._id || res._id)) {
            this.formId = doc?._id || res._id;
            this.isEditMode = true;
          }
          this.formSaved.emit({ data: formData, fields: this.formFields });
          this._utils.showErrorSuccess(wasNew ? 'Form saved successfully!' : 'Form updated successfully!');
          this.router.navigate(['/Dynamic-Form']);
        } else {
          this._utils.showErrorInfo('Something went wrong!');
        }
      },
      error: (e) => {
        this.isLoading = false;
        this._utils.showErrorInfo(this.isEditMode ? 'Failed to update form. Please try again.' : 'Failed to save form. Please try again.');
      },
      complete: () => {
        if (this.saveSubscription) {
          this.saveSubscription.unsubscribe();
        }
      }
    });
  }

  dynamicFormUI() {
    this.router.navigate(['/Dynamic-Form']);
  }

  ngOnDestroy(): void {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
    if (this.getFormSubscription) {
      this.getFormSubscription.unsubscribe();
    }
    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }
  }

  goBack() {
    window.history.back();
  }

  getFieldIcon(type: string): string {
    const fieldType = this.availableFieldTypes.find(ft => ft.type === type);
    return fieldType ? fieldType.icon : 'fa fa-font';
  }

  isFieldTypeWithOptions(type: string): boolean {
    return type === 'select' || type === 'radio';
  }

  onIncidentBindChange(bindType: 'user' | 'team' | 'speaker' | 'operator', checked: boolean): void {
    if (this.editingSectionIndex === null || this.editingFieldIndex === null) {
      return;
    }
    if (checked) {
      this.fieldConfig.incidentBindUser = bindType === 'user';
      this.fieldConfig.incidentBindTeam = bindType === 'team';
      this.fieldConfig.incidentBindSpeaker = bindType === 'speaker';
      this.fieldConfig.incidentBindOperator = bindType === 'operator';
    } else if (bindType === 'user') {
      this.fieldConfig.incidentBindUser = false;
    } else if (bindType === 'team') {
      this.fieldConfig.incidentBindTeam = false;
    } else if (bindType === 'speaker') {
      this.fieldConfig.incidentBindSpeaker = false;
    } else {
      this.fieldConfig.incidentBindOperator = false;
    }
    this.updateFieldInPreview(this.editingSectionIndex, this.editingFieldIndex);
  }

  isFieldTypeWithRange(type: string): boolean {
    return type === 'number' || type === 'range';
  }

  toggleSection(section: keyof typeof this.expandedSections) {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  getColumnClass(columns: string | undefined): string {
    if (!columns || columns === 'auto') {
      return 'col-auto';
    }
    return `col-${columns}`;
  }

  getColumnStyle(columns: string | undefined): any {
    if (!columns || columns === 'auto') {
      return {
        'flex': '0 0 100%',
        'max-width': '100%',
        'width': '100%'
      };
    }
    const colValue = parseInt(columns);
    if (isNaN(colValue)) {
      return {};
    }
    const percentage = (colValue / 12) * 100;
    const gap = 15;
    const itemsPerRow = 12 / colValue;
    const gapsPerRow = itemsPerRow - 1;
    const totalGapSpace = gapsPerRow * gap;
    const adjustedPercentage = `calc((100% - ${totalGapSpace}px) / ${itemsPerRow})`;

    return {
      'flex': `0 0 ${adjustedPercentage}`,
      'max-width': adjustedPercentage,
      'min-width': adjustedPercentage,
      'width': adjustedPercentage
    };
  }

  toggleMultiSelect(fieldName: string) {
    this.multiSelectOpen[fieldName] = !this.multiSelectOpen[fieldName];
    if (this.multiSelectOpen[fieldName]) {
      this.multiSelectSearch[fieldName] = '';
    }
  }

  openMultiSelect(fieldName: string) {
    this.multiSelectOpen[fieldName] = true;
    if (!this.multiSelectSearch[fieldName]) {
      this.multiSelectSearch[fieldName] = '';
    }
  }

  closeMultiSelect(fieldName: string) {
    this.multiSelectOpen[fieldName] = false;
    this.multiSelectSearch[fieldName] = '';
  }

  closeMultiSelectDelayed(fieldName: string) {
    setTimeout(() => {
      this.closeMultiSelect(fieldName);
    }, 200);
  }

  getMultiSelectOpenState(fieldName: string): boolean {
    return this.multiSelectOpen[fieldName] || false;
  }

  getSelectedValues(fieldName: string): string[] {
    const control = this.form.get(fieldName);
    if (control && control.value) {
      return Array.isArray(control.value) ? control.value : [control.value].filter(v => v);
    }
    return [];
  }

  isOptionSelected(fieldName: string, option: string): boolean {
    const selected = this.getSelectedValues(fieldName);
    return selected.includes(option);
  }

  toggleMultiSelectOption(fieldName: string, option: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    const control = this.form.get(fieldName);
    if (!control) return;

    const currentValue = control.value || [];
    const selected = Array.isArray(currentValue) ? currentValue : [currentValue].filter(v => v);

    const index = selected.indexOf(option);
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(option);
    }

    control.setValue(selected);
    control.markAsTouched();
  }

  removeMultiSelectOption(fieldName: string, option: string, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    const control = this.form.get(fieldName);
    if (!control) return;

    const currentValue = control.value || [];
    const selected = Array.isArray(currentValue) ? currentValue : [currentValue].filter(v => v);

    const index = selected.indexOf(option);
    if (index > -1) {
      selected.splice(index, 1);
      control.setValue(selected);
      control.markAsTouched();
    }
  }

  getFilteredOptions(field: FormField): string[] {
    if (!field.options) return [];
    const searchTerm = (this.multiSelectSearch[field.name] || '').toLowerCase();
    if (!searchTerm) return field.options;
    return field.options.filter(opt => opt.toLowerCase().includes(searchTerm));
  }

  handleMultiSelectKeydown(event: KeyboardEvent, fieldName: string) {
    if (event.key === 'Escape') {
      this.closeMultiSelect(fieldName);
    }
  }

  shouldShowError(fieldName: string): boolean {
    if (this.formSubmitted !== true) {
      return false;
    }
    if (!this.form) {
      return false;
    }
    const control = this.form.get(fieldName);
    if (!control) {
      return false;
    }
    if (control.disabled) {
      return false;
    }
    if (!control.errors || Object.keys(control.errors).length === 0) {
      return false;
    }
    return control.invalid === true && control.touched === true;
  }

  getErrorMessage(field: FormField, errorKey: string, errorValue?: any): string {
    if (field.errorMessages && field.errorMessages[errorKey as keyof typeof field.errorMessages]) {
      let message = field.errorMessages[errorKey as keyof typeof field.errorMessages] || '';
      if (errorValue && typeof message === 'string') {
        message = message.replace('{{value}}', errorValue);
        message = message.replace('{{requiredLength}}', errorValue?.requiredLength || errorValue);
        message = message.replace('{{min}}', errorValue?.min || errorValue);
        message = message.replace('{{max}}', errorValue?.max || errorValue);
      }
      return message;
    }

    const defaultMessages: { [key: string]: string } = {
      required: 'This field is required',
      email: 'Invalid email format',
      pattern: 'Invalid format',
      minlength: `Minimum length is ${errorValue?.requiredLength || ''}`,
      maxlength: `Maximum length is ${errorValue?.requiredLength || ''}`,
      min: `Minimum value is ${errorValue?.min || errorValue || ''}`,
      max: `Maximum value is ${errorValue?.max || errorValue || ''}`
    };

    return defaultMessages[errorKey] || 'Invalid value';
  }

  getAvailableFieldsForCondition(): FormField[] {
    if (this.editingSectionIndex === null || this.editingFieldIndex === null) return [];
    return this.formFields.filter((f, idx) => {
      const pos = this.getSectionAndFieldIndex(idx);
      return !(pos.si === this.editingSectionIndex && pos.fi === this.editingFieldIndex);
    });
  }

  getSectionAndFieldIndex(flatIndex: number): { si: number; fi: number } {
    if (this.formSections.length === 0) {
      return { si: this.UNSECTIONED_INDEX, fi: flatIndex };
    }
    let idx = 0;
    for (let s = 0; s < this.formSections.length; s++) {
      const len = this.formSections[s].fields.length;
      if (flatIndex < idx + len) return { si: s, fi: flatIndex - idx };
      idx += len;
    }
    return { si: 0, fi: 0 };
  }

  shouldFieldBeVisible(field: FormField, index: number): boolean {
    return true;
  }

  shouldFieldBeConditionallyHidden(field: FormField, index: number): boolean {
    return !shouldShowFieldByConditions(
      field,
      (fieldName) => this.formFields.find((f) => f.name === fieldName),
      (fieldName) => this.form.get(fieldName)?.value
    );
  }

  ensureFieldConfigConditions(): void {
    if (!this.fieldConfig.conditions) {
      this.fieldConfig.conditions = [];
    }
    if (!this.fieldConfig.conditionLogic) {
      this.fieldConfig.conditionLogic = 'and';
    }
    if (this.fieldConfig.conditions.length === 0 && this.fieldConfig.conditionFieldName) {
      this.fieldConfig.conditions = [{
        fieldName: this.fieldConfig.conditionFieldName,
        action: this.fieldConfig.conditionAction || 'show',
        expectedValue: '__any__'
      }];
    }
  }

  isFieldConfigPermanentlyHidden(): boolean {
    this.ensureFieldConfigConditions();
    return isFieldPermanentlyHidden(this.fieldConfig);
  }

  addConditionRule(): void {
    this.ensureFieldConfigConditions();
    this.fieldConfig.conditions = [
      ...(this.fieldConfig.conditions || []),
      { fieldName: '', action: 'show', expectedValue: 'yes' }
    ];
    this.onConditionsChange();
  }

  removeConditionRule(index: number): void {
    this.ensureFieldConfigConditions();
    this.fieldConfig.conditions = (this.fieldConfig.conditions || []).filter((_, i) => i !== index);
    this.onConditionsChange();
  }

  onConditionFieldNameChange(conditionIndex: number, fieldName: string): void {
    this.ensureFieldConfigConditions();
    const rules = this.fieldConfig.conditions || [];
    if (!rules[conditionIndex]) {
      return;
    }
    rules[conditionIndex].fieldName = fieldName;
    const availableValues = this.getConditionValueOptions(rules[conditionIndex]).map((option) => option.value);
    const currentValue = this.normalizeConditionExpectedValue(rules[conditionIndex].expectedValue);
    if (!availableValues.includes(currentValue)) {
      rules[conditionIndex].expectedValue = availableValues[0] || 'yes';
    }
    this.onConditionsChange();
  }

  onConditionExpectedValueChange(conditionIndex: number, value: string): void {
    this.ensureFieldConfigConditions();
    const rules = this.fieldConfig.conditions || [];
    if (!rules[conditionIndex]) {
      return;
    }
    rules[conditionIndex].expectedValue = value;
    this.onConditionsChange();
  }

  onConditionsChange(): void {
    this.ensureFieldConfigConditions();
    const rules = (this.fieldConfig.conditions || []).filter((rule) => !!rule.fieldName);
    if (rules.length === 1 && rules[0].fieldName === '__hide__') {
      this.fieldConfig.conditionFieldName = '__hide__';
      this.fieldConfig.conditionAction = 'hide';
    } else if (rules.length > 0) {
      this.fieldConfig.conditionFieldName = rules[0].fieldName;
      this.fieldConfig.conditionAction = rules[0].action || 'show';
    } else {
      this.fieldConfig.conditionFieldName = '';
      this.fieldConfig.conditionAction = 'hide';
    }
    if (this.editingSectionIndex !== null && this.editingFieldIndex !== null) {
      this.updateFieldInPreview(this.editingSectionIndex, this.editingFieldIndex);
    }
  }

  setFieldConfigHidden(hidden: boolean): void {
    this.ensureFieldConfigConditions();
    if (hidden) {
      this.fieldConfig.conditions = [{ fieldName: '__hide__', action: 'hide' }];
      this.fieldConfig.conditionFieldName = '__hide__';
      this.fieldConfig.conditionAction = 'hide';
    } else if (this.fieldConfig.conditions?.length === 1 && this.fieldConfig.conditions[0].fieldName === '__hide__') {
      this.fieldConfig.conditions = [{ fieldName: '', action: 'show', expectedValue: 'yes' }];
      this.fieldConfig.conditionFieldName = '';
      this.fieldConfig.conditionAction = 'show';
    }
    this.onConditionsChange();
  }

  getConditionValueOptions(rule?: FormFieldConditionRule): Array<{ value: string; label: string }> {
    const fieldSpecificOptions = this.getConditionFieldValueOptions(rule?.fieldName);
    if (fieldSpecificOptions.length) {
      return [
        ...fieldSpecificOptions,
        { value: '__any__', label: 'Any value' },
        { value: '__has_value__', label: 'Has value' },
        { value: '__empty__', label: 'Is empty' }
      ];
    }
    return this.baseConditionValueOptions;
  }

  private getConditionFieldValueOptions(fieldName?: string): Array<{ value: string; label: string }> {
    if (!fieldName) {
      return [];
    }
    const field = this.formFields.find((item) => item.name === fieldName);
    if (!field || (field.type !== 'select' && field.type !== 'radio') || !field.options?.length) {
      return [];
    }
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [];
    field.options.forEach((rawOpt) => {
      if (rawOpt === null || rawOpt === undefined) {
        return;
      }
      const opt = rawOpt as string | { label?: string; value?: string };
      const label = typeof opt === 'object' ? String(opt.label ?? opt.value ?? '').trim() : String(opt).trim();
      const value = typeof opt === 'object' ? String(opt.value ?? opt.label ?? '').trim() : String(opt).trim();
      if (!value || seen.has(value.toLowerCase())) {
        return;
      }
      seen.add(value.toLowerCase());
      options.push({ value, label: label || value });
    });
    return options;
  }

  private cloneConditionRules(rules?: FormFieldConditionRule[]): FormFieldConditionRule[] {
    return (rules || []).map((rule) => ({
      ...rule,
      expectedValue: this.normalizeConditionExpectedValue(rule.expectedValue)
    }));
  }

  private normalizeConditionExpectedValue(value?: string): string {
    if (value === undefined || value === null) {
      return 'yes';
    }
    if (value === '') {
      return '__any__';
    }
    return value;
  }

  private onConditionFieldChange() {
    if (this.fieldConfig.conditionFieldName === '__hide__') {
      this.setFieldConfigHidden(true);
      return;
    }
    this.ensureFieldConfigConditions();
    if (!this.fieldConfig.conditions?.length) {
      this.fieldConfig.conditions = [{
        fieldName: this.fieldConfig.conditionFieldName || '',
        action: 'show',
        expectedValue: 'yes'
      }];
    }
    if (this.fieldConfig.conditionFieldName && this.fieldConfig.conditionFieldName !== '__hide__') {
      if (!this.fieldConfig.conditionAction || this.fieldConfig.conditionAction === 'hide') {
        this.fieldConfig.conditionAction = 'show';
      }
    }
    this.onConditionsChange();
  }

  isEditing(sectionIndex: number, fieldIndex: number): boolean {
    return this.editingSectionIndex === sectionIndex && this.editingFieldIndex === fieldIndex;
  }

  isDragOver(sectionIndex: number, fieldIndex: number): boolean {
    return this.dragOverSectionIndex === sectionIndex && this.dragOverFieldIndex === fieldIndex;
  }

  isDragging(sectionIndex: number, fieldIndex: number): boolean {
    return this.draggedSectionIndex === sectionIndex && this.draggedFieldIndex === fieldIndex;
  }

  trackByConditionOptionValue(_index: number, option: { value: string; label: string }): string {
    return option.value;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
